import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "./AppSidebar";
import { API } from "./cache";
import DateStrip from "./features/common/components/DateStrip";
import DrawerTabs from "./features/common/components/DrawerTabs";
import "./MeetingsViewV2.css";
import "./V2SideDrawer.css";

const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MON = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const MEETING_STATUSES = ["Scheduled", "In Progress", "Completed", "Cancelled"];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dateFromKey(key) {
  const [y, m, d] = String(key).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function stripMetrics(d) {
  const weekend = d.getDay() === 0 || d.getDay() === 6;
  return { weekend };
}

const PLATFORMS = {
  teams: { label: "Teams", cls: "teams" },
  zoom: { label: "Zoom", cls: "zoom" },
  meet: { label: "Meet", cls: "meet" },
  in: { label: "In-person · CF-BLR-06", cls: "in" },
};
const TEAMS_RELOGIN_RESUME_KEY = "protrack_teams_relogin_resume";
const TEAMS_CACHE_TTL_MS = 8 * 60 * 60 * 1000;
const MY_MEETINGS_CACHE_TTL_MS = 30 * 60 * 1000;
const PARTICIPATION_STATUS_OPTIONS = ["No Response", "Tentative", "Accepted", "Declined", "Attended"];

function startMicrosoftLoginForTeams(dateStr) {
  try {
    localStorage.setItem(TEAMS_RELOGIN_RESUME_KEY, JSON.stringify({
      route: "/meetings",
      tab: "team",
      date: dateStr,
      force: true,
      savedAt: Date.now(),
    }));
  } catch {}
  window.location.href = `${API}/login`;
}

function initials(name) {
  const n = String(name || "").trim();
  if (!n) return "NA";
  return n.split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

function toLocalHM(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return ""; }
}

function toDateLabel(isoDate) {
  if (!isoDate) return "";
  try {
    return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase();
  } catch { return isoDate; }
}

function formatSyncedAgo(ts, nowTs) {
  if (!ts) return "OUTLOOK · NOT SYNCED";
  const diffMs = Math.max(0, nowTs - ts);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "OUTLOOK · SYNCED JUST NOW";
  if (mins < 60) return `OUTLOOK · SYNCED ${mins}M AGO`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `OUTLOOK · SYNCED ${hrs}H AGO`;
  const days = Math.floor(hrs / 24);
  return `OUTLOOK · SYNCED ${days}D AGO`;
}

function attendeeStatusForCurrentUser(meeting, userEmail) {
  const email = String(userEmail || "").trim().toLowerCase();
  if (!email) return "";
  const participants = Array.isArray(meeting?.participants) ? meeting.participants : [];
  const me = participants.find((p) => String(p?.email || "").trim().toLowerCase() === email);
  return String(me?.attendance_status || "").trim().toLowerCase();
}

function bookedMinutesFromMeetings(meetings, userEmail) {
  return (Array.isArray(meetings) ? meetings : []).reduce((sum, m) => {
    const status = attendeeStatusForCurrentUser(m, userEmail);
    if (status === "declined") return sum;
    const mins = Number(m?.durMin) || 0;
    return sum + Math.max(0, mins);
  }, 0);
}

function countMeetingConflicts(meetings) {
  const intervals = (Array.isArray(meetings) ? meetings : [])
    .map((m) => ({ s: Number(m?.startMs), e: Number(m?.endMs) }))
    .filter((x) => Number.isFinite(x.s) && Number.isFinite(x.e) && x.e > x.s)
    .sort((a, b) => a.s - b.s);
  let conflicts = 0;
  for (let i = 0; i < intervals.length; i += 1) {
    for (let j = i + 1; j < intervals.length; j += 1) {
      if (intervals[j].s >= intervals[i].e) break;
      if (intervals[j].e > intervals[i].s) conflicts += 1;
    }
  }
  return conflicts;
}

function classifyMeetingStatus(m) {
  const now = Date.now();
  const s = m?.start_utc ? new Date(m.start_utc).getTime() : NaN;
  const e = m?.end_utc ? new Date(m.end_utc).getTime() : NaN;
  if (Number.isFinite(s) && Number.isFinite(e)) {
    if (now >= s && now <= e) return "now";
    if (now < s) return (s - now <= 30 * 60 * 1000) ? "next" : "upcoming";
    return "past";
  }
  const raw = String(m?.status || "").toLowerCase();
  if (raw === "in progress") return "now";
  if (raw === "completed") return "past";
  return "upcoming";
}

function parseLocalDateTimeMs(dateIso, hhmm) {
  if (!dateIso || !hhmm) return NaN;
  const [h, mm] = String(hhmm).split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return NaN;
  const d = new Date(`${dateIso}T00:00:00`);
  d.setHours(h, mm, 0, 0);
  return d.getTime();
}

function minutesBetween(startHm, endHm) {
  const [sh, sm] = String(startHm || "").split(":").map(Number);
  const [eh, em] = String(endHm || "").split(":").map(Number);
  if (![sh, sm, eh, em].every(Number.isFinite)) return 0;
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
}

function platformFromMeetingLink(link = "") {
  const l = String(link || "").toLowerCase();
  if (l.includes("teams")) return "teams";
  if (l.includes("zoom")) return "zoom";
  if (l.includes("meet.google")) return "meet";
  return "in";
}

function classifyMyMeetingStatus(m) {
  const raw = String(m?.status || "").toLowerCase();
  if (raw === "completed") return "past";
  if (raw === "in progress") return "now";
  const s = Number(m?.startMs);
  const e = Number(m?.endMs);
  const now = Date.now();
  if (Number.isFinite(s) && Number.isFinite(e)) {
    if (now >= s && now <= e) return "now";
    if (now < s) return (s - now <= 30 * 60 * 1000) ? "next" : "upcoming";
    return "past";
  }
  return "upcoming";
}

function normalizeMyMeetings(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter((m) => String(m?.status || "").toLowerCase() !== "cancelled")
    .map((m, idx) => {
      const from = String(m?.start_time || "").slice(0, 5);
      const to = String(m?.end_time || "").slice(0, 5);
      const startMs = parseLocalDateTimeMs(m?.meeting_date, from);
      const endMs = parseLocalDateTimeMs(m?.meeting_date, to);
      const durMin = minutesBetween(from, to);
      const totalParticipants = Number(m?.participant_count || 0);
      const platform = platformFromMeetingLink(m?.meeting_link);
      const mapped = {
        id: `ME-${String(idx + 1).padStart(2, "0")}`,
        key: m.id || `my-${idx}`,
        rawId: m.id || "",
        organizerId: m.organizer_id || "",
        rawStatus: m.status || "",
        title: m.title || "(No title)",
        from,
        to,
        durMin,
        startMs,
        endMs,
        owner: m.organizer_name || m.assigned_by || "—",
        ownerInit: initials(m.organizer_name || m.assigned_by || ""),
        platform,
        project: m.project_names || m.location || m.meeting_type_name || "Meeting",
        status: "upcoming",
        acc: totalParticipants > 0 ? totalParticipants : 1,
        pend: 0,
        dec: 0,
        preview: m.description || "Meeting",
        meetingLink: m.meeting_link || "",
        meetingDate: m.meeting_date || "",
        startUtc: "",
        endUtc: "",
        participants: [],
        source: "my",
      };
      mapped.status = classifyMyMeetingStatus(mapped);
      return mapped;
    })
    .sort((a, b) => a.from.localeCompare(b.from));
}

function normalizeTeamsMeetings(items = []) {
  return (items || [])
    .filter((m) => String(m?.status || "").toLowerCase() !== "cancelled")
    .map((m, idx) => {
      const participants = Array.isArray(m.participants) ? m.participants : [];
      const acc = participants.filter((p) => ["accepted", "attended"].includes(String(p.attendance_status || "").toLowerCase())).length;
      const pend = participants.filter((p) => ["no response", "tentative"].includes(String(p.attendance_status || "").toLowerCase())).length;
      const dec = participants.filter((p) => String(p.attendance_status || "").toLowerCase() === "declined").length;
      const count = Number(m.participant_count || participants.length || 0);
      const accF = acc || (count > 0 ? count : 0);
      const pendF = pend || 0;
      const decF = dec || 0;
      const from = toLocalHM(m.start_utc);
      const to = toLocalHM(m.end_utc);
      const durMin = (() => {
        const s = m?.start_utc ? new Date(m.start_utc).getTime() : NaN;
        const e = m?.end_utc ? new Date(m.end_utc).getTime() : NaN;
        return Number.isFinite(s) && Number.isFinite(e) ? Math.max(0, Math.round((e - s) / 60000)) : 0;
      })();
      const startMs = m?.start_utc ? new Date(m.start_utc).getTime() : NaN;
      const endMs = m?.end_utc ? new Date(m.end_utc).getTime() : NaN;
      return {
        id: `TM-${String(idx + 1).padStart(2, "0")}`,
        key: m.id || `tm-${idx}`,
        rawId: m.id || "",
        organizerId: m.organizer_id || "",
        // Teams (calendar-synced) meetings have no local meetings-table row,
        // so there's no organizer_id UUID to resolve — only organizer_email
        // (from the Graph event itself). isCreator falls back to matching
        // this against the current user's own email for these meetings.
        organizerEmail: m.organizer_email || "",
        rawStatus: m.status || "",
        title: m.title || "(No title)",
        from,
        to,
        durMin,
        startMs,
        endMs,
        owner: m.organizer_name || "—",
        ownerInit: initials(m.organizer_name),
        platform: "teams",
        project: m.location || "Teams Meeting",
        status: classifyMeetingStatus(m),
        acc: accF,
        pend: pendF,
        dec: decF,
        preview: m.description || m.meeting_link || "Teams meeting",
        meetingLink: m.meeting_link || "",
        meetingDate: m.meeting_date || localDateFromUtc(m.start_utc),
        startUtc: m.start_utc || "",
        endUtc: m.end_utc || "",
        participants,
        source: "team",
      };
    })
    .sort((a, b) => a.from.localeCompare(b.from));
}

function tm(t) {
  const [h, m] = t.split(":").map(Number);
  const pct = (((h + m / 60) - 6) / 16) * 100;
  return Math.max(0, Math.min(100, pct));
}

function statusChip(status) {
  if (status === "now") return <span className="mtv2-status mtv2-status--red">Live now</span>;
  if (status === "next") return <span className="mtv2-status mtv2-status--amber">Up next</span>;
  if (status === "past") return <span className="mtv2-status mtv2-status--ghost">Completed</span>;
  return <span className="mtv2-status mtv2-status--blue">Scheduled</span>;
}

function MeetingRow({ m, onOpenDetails }) {
  const p = PLATFORMS[m.platform];
  const total = m.acc + m.pend + m.dec;
  const past = m.status === "past";

  return (
    <div className={`mtv2-row${m.status === "now" ? " now" : ""}`}>
      <div className="mtv2-time">
        <div className="from">{m.from}</div>
        <div className="to">→ {m.to}</div>
        <div className="dur">{m.durMin}m</div>
      </div>

      <div className="mtv2-info">
        <div className="title">
          <span className="mtv2-num">{m.id}</span>
          <span>{m.title}</span>
          {m.highlight && <span className="mtv2-tag mtv2-tag--amber">NSCCL</span>}
        </div>
        <div className="subline">
          <span className="owner-tag">
            <span className="avatar">{m.ownerInit}</span>
            {m.owner}
          </span>
          <span className="sep">·</span>
          <span>{m.project}</span>
          <span className="sep">·</span>
          {statusChip(m.status)}
        </div>
        <div className="preview">{m.preview}</div>
      </div>

      <div className="mtv2-attendees">
        <div className="mtv2-bar">
          <i className="acc" style={{ width: `${(m.acc / total) * 100}%` }} />
          <i className="pend" style={{ width: `${(m.pend / total) * 100}%` }} />
          <i className="dec" style={{ width: `${(m.dec / total) * 100}%` }} />
        </div>
        <div className="stats">
          <div className="acc"><b>{m.acc}</b>acc</div>
          <div className="pend"><b>{m.pend}</b>pend</div>
          <div className="dec"><b>{m.dec}</b>dec</div>
          <div className="tot"><b>{total}</b>tot</div>
        </div>
      </div>

      <div className="mtv2-chips">
        <span className={`mtv2-platform ${p.cls}`}>
          <span className="pmark" />
          {p.label}
        </span>
        <span className="mtv2-tag">{m.project}</span>
      </div>

      <div className="mtv2-actions">
        {past ? (
          <>
            <button className="mtv2-join ghost" type="button">Recording</button>
            <span className="mtv2-more">Notes · Transcript</span>
          </>
        ) : (
          <>
            {m.meetingLink ? (
              <a className="mtv2-join" href={m.meetingLink} target="_blank" rel="noreferrer">
                {m.status === "now" ? "Join now" : "Join"}
              </a>
            ) : (
              <button className="mtv2-join" type="button">{m.status === "now" ? "Join now" : "Join"}</button>
            )}
            <span className="mtv2-more">Agenda · Reschedule</span>
          </>
        )}
        <button className="mtv2-detail-btn" type="button" onClick={() => onOpenDetails?.(m)}>
          Details
        </button>
      </div>
    </div>
  );
}

function localDateFromUtc(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch { return ""; }
}

function meetingDateLabel(m) {
  const isoDate = m?.meetingDate || localDateFromUtc(m?.startUtc);
  if (!isoDate) return "—";
  try {
    return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return isoDate; }
}

function formatSecondsAsHm(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safe / 3600);
  const mins = Math.floor((safe % 3600) / 60);
  return `${hours}:${String(mins).padStart(2, "0")}`;
}

function parseHmToSeconds(raw) {
  const v = String(raw || "").trim();
  if (!v) return null;
  const m = v.match(/^(\d+):([0-5]\d)$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  return (hh * 3600) + (mm * 60);
}

function parseDurationTextToSeconds(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (!v) return 0;
  const hm = v.match(/^(\d+):([0-5]\d)$/);
  if (hm) return (Number(hm[1]) * 3600) + (Number(hm[2]) * 60);
  const h = (v.match(/(\d+)\s*h/) || [])[1];
  const m = (v.match(/(\d+)\s*m/) || [])[1];
  return ((Number(h) || 0) * 3600) + ((Number(m) || 0) * 60);
}

function meetingRangeSeconds(meeting) {
  const mins = minutesBetween(meeting?.from || "", meeting?.to || "");
  return Math.max(0, mins * 60);
}

function participantRowKey(p, idx) {
  const pid = String(p?.participant_id || "").trim();
  if (pid) return `pid:${pid}`;
  const email = String(p?.email || "").trim().toLowerCase();
  if (email) return `mail:${email}`;
  return `idx:${idx}`;
}

function normalizeMeetingDetailParticipants(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((p) => {
    const firstJoin = p?.attendance_first_join_utc ? new Date(p.attendance_first_join_utc).getTime() : NaN;
    const lastLeave = p?.attendance_last_leave_utc ? new Date(p.attendance_last_leave_utc).getTime() : NaN;
    let seconds = Number(p?.attendance_seconds);
    if (!Number.isFinite(seconds) && Number.isFinite(firstJoin) && Number.isFinite(lastLeave) && lastLeave > firstJoin) {
      seconds = Math.round((lastLeave - firstJoin) / 1000);
    }
    const duration = Number.isFinite(seconds)
      ? `${Math.floor(seconds / 3600)}h ${String(Math.floor((seconds % 3600) / 60)).padStart(2, "0")}m`
      : (p?.attendance_duration || "—");

    return {
      _meetingParticipantId: p?.id || "",
      participant_id: p?.participant_id || "",
      name: p?.display_name || p?.participant_name || p?.name || "",
      email: p?.user_name || p?.email || "",
      role: p?.role || "Required",
      attendance_status: p?.attendance_status || "No Response",
      attendance_duration: duration,
      attendance_seconds: Number.isFinite(seconds) ? seconds : 0,
    };
  });
}

function TeamsMeetingDrawer({
  meeting,
  internalUsers,
  meetingTypes = [],
  projects = [],
  user,
  onClose,
  onRefreshMeetingDetails,
  onAfterEdit,
}) {
  const [tab, setTab] = useState("info");
  const [tsSubmitting, setTsSubmitting] = useState(false);
  const [tsMsg, setTsMsg] = useState("");
  const [tsWarn, setTsWarn] = useState(false);
  const [timeInputs, setTimeInputs] = useState({});
  const [statusInputs, setStatusInputs] = useState({});
  const [internalTimesheetProjectId, setInternalTimesheetProjectId] = useState("");
  const [internalTimesheetEpicId, setInternalTimesheetEpicId] = useState("");
  const [timesheetEpics, setTimesheetEpics] = useState([]);
  const [timesheetEpicsLoading, setTimesheetEpicsLoading] = useState(false);
  const [addParticipantIds, setAddParticipantIds] = useState([]);
  const [addingParticipants, setAddingParticipants] = useState(false);
  const [addParticipantErr, setAddParticipantErr] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const hasParticipants = Array.isArray(meeting?.participants) && meeting.participants.length > 0;

  const participantsWithKeys = useMemo(
    () => (Array.isArray(meeting?.participants) ? meeting.participants : []).map((p, i) => ({ ...p, _rowKey: participantRowKey(p, i) })),
    [meeting]
  );

  const { internal, external } = useMemo(() => {
    if (!hasParticipants || tab !== "participants") {
      return { internal: [], external: [] };
    }
    const internalByIdMap = {};
    const internalEmailMap = {};
    (internalUsers || []).forEach((u) => {
      if (u?.id) internalByIdMap[String(u.id).toLowerCase()] = u;
      if (u?.user_name) internalEmailMap[String(u.user_name).toLowerCase()] = u;
    });
    const int_ = [];
    const ext_ = [];
    participantsWithKeys.forEach((p) => {
      const pid = String(p?.participant_id || "").toLowerCase();
      const key = String(p?.email || "").toLowerCase();
      const matched = (pid && internalByIdMap[pid]) || (key && internalEmailMap[key]) || null;
      if (matched) {
        int_.push({
          ...p,
          _displayName: matched.display_name || p.name,
          _email: p.email || matched.user_name || "",
          _isInternal: true,
        });
      } else {
        ext_.push({
          ...p,
          _displayName: p.name || p.email || "—",
          _email: p.email || "",
          _isInternal: false,
        });
      }
    });
    return { internal: int_, external: ext_ };
  }, [participantsWithKeys, internalUsers, hasParticipants, tab]);
  // Keyed by _rowKey, not participant_id — Teams-meeting participants come
  // from Microsoft Graph and only ever have an email (participant_id only
  // exists for organizer-created "my" meetings, via meeting_participants).
  // The `internal` array above already correctly matches by email OR
  // participant_id; _rowKey is the one identifier stable across both cases.
  const internalRowKeySet = useMemo(
    () => new Set(internal.map((p) => p?._rowKey).filter(Boolean)),
    [internal]
  );

  const isMyMeeting = meeting?.source === "my";
  const canSubmitTimesheet = isMyMeeting
    ? !!meeting?.rawId
    : String(meeting?.rawStatus || "").toLowerCase() === "completed";
  const isAdminUser = ["admin", "super admin"].includes(String(user?.db_role || "").toLowerCase());
  const isCreator = (() => {
    const currentUserId = String(user?.db_user_id || "").trim();
    const meetingOrganizerId = String(meeting?.organizerId || "").trim();
    if (currentUserId && meetingOrganizerId && currentUserId === meetingOrganizerId) return true;
    // Teams (calendar-synced) meetings have no local organizer_id (the event
    // isn't backed by a meetings-table row) — only organizer_email, resolved
    // from the Graph event itself. Fall back to matching that against the
    // current session's own email so a non-admin Teams-meeting organizer can
    // still edit/submit their own meeting's timesheet.
    const currentUserEmail = String(user?.email || user?.user_name || user?.preferred_username || "").trim().toLowerCase();
    const meetingOrganizerEmail = String(meeting?.organizerEmail || "").trim().toLowerCase();
    return !!currentUserEmail && !!meetingOrganizerEmail && currentUserEmail === meetingOrganizerEmail;
  })();
  const canEditParticipation = canSubmitTimesheet && (isAdminUser || isCreator);
  const canManageParticipants = isMyMeeting && !!meeting?.rawId && (isAdminUser || isCreator);
  const canEditMeeting = canManageParticipants;

  const projectOptions = useMemo(
    () =>
      (Array.isArray(projects) ? projects : []).map((p) => ({
        value: p.project_id,
        label: `${p.project_name}${p.customer_name ? ` (${p.customer_name})` : ""}`,
      })),
    [projects]
  );

  const epicOptions = useMemo(
    () =>
      (Array.isArray(timesheetEpics) ? timesheetEpics : []).map((e) => ({
        value: e.id,
        label: e.epic_name || e.epic_code || "—",
      })),
    [timesheetEpics]
  );

  const enterEditMode = () => {
    if (!canEditMeeting) return;
    const currentParticipantIds = participantsWithKeys
      .map((p) => String(p?.participant_id || "").trim())
      .filter(Boolean);
    setEditForm({
      title: meeting?.title || "",
      description: meeting?.description || meeting?.preview || "",
      meeting_type_id: meeting?.meetingTypeId || "",
      meeting_date: meeting?.meetingDate || "",
      start_time: meeting?.startTime || "",
      end_time: meeting?.endTime || "",
      meeting_link: meeting?.meetingLink || "",
      status: meeting?.rawStatus || "Scheduled",
      participant_ids: currentParticipantIds,
      project_id: (meeting?.projectIds || [])[0] || "",
    });
    setEditError("");
    setEditing(true);
  };

  const exitEditMode = () => {
    setEditing(false);
    setEditForm(null);
    setEditError("");
  };

  const setEditField = (key, value) =>
    setEditForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const saveMeetingEdit = async () => {
    if (!editForm || !meeting?.rawId) return;
    if (!editForm.title.trim() || !editForm.meeting_date || !editForm.start_time) {
      setEditError("Title, Date and Start Time are required.");
      return;
    }
    const meetingId = meeting.rawId;
    const resolvedEndTime = editForm.end_time || editForm.start_time;
    const nextParticipantIds = (editForm.participant_ids || []).map((v) => String(v));
    const currentParticipants = participantsWithKeys.filter((p) => p?.participant_id);

    setEditSaving(true);
    setEditError("");
    try {
      const putRes = await fetch(`${API}/meetings/${meetingId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: editForm.title,
          description: editForm.description,
          meeting_type_id: editForm.meeting_type_id || null,
          meeting_date: editForm.meeting_date,
          start_time: editForm.start_time,
          end_time: resolvedEndTime,
          organizer_id: meeting?.organizerId || user?.db_user_id || "",
          meeting_link: editForm.meeting_link,
          status: editForm.status,
        }),
      });
      if (!putRes.ok) {
        const data = await putRes.json().catch(() => ({}));
        throw new Error(data?.detail || `Error ${putRes.status}`);
      }

      // Sync participants — diff add/remove instead of full reset so attendance survives
      const nextSet = new Set(nextParticipantIds);
      const removed = currentParticipants.filter(
        (p) => !nextSet.has(String(p.participant_id))
      );
      const addedIds = nextParticipantIds.filter(
        (pid) => !currentParticipants.some((p) => String(p.participant_id) === pid)
      );
      await Promise.all(
        removed.map((p) =>
          fetch(`${API}/meeting-participants/${p.id}`, {
            method: "DELETE",
            credentials: "include",
          }).catch(() => null)
        )
      );
      await Promise.all(
        addedIds.map((pid) =>
          fetch(`${API}/meetings/${meetingId}/participants`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              participant_id: pid,
              role: "Required",
              attendance_status: "No Response",
            }),
          }).catch(() => null)
        )
      );

      // Bulk-replace projects
      await fetch(`${API}/meetings/${meetingId}/projects`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_ids: editForm.project_id ? [editForm.project_id] : [],
        }),
      }).catch(() => null);

      await onRefreshMeetingDetails?.(meetingId);
      await onAfterEdit?.(editForm.meeting_date);
      exitEditMode();
    } catch (e) {
      setEditError(e?.message || "Failed to update meeting");
    } finally {
      setEditSaving(false);
    }
  };

  const existingParticipantIds = useMemo(() => {
    const set = new Set();
    participantsWithKeys.forEach((p) => {
      const pid = String(p?.participant_id || "").trim();
      if (pid) set.add(pid);
    });
    return set;
  }, [participantsWithKeys]);

  const addableParticipants = useMemo(
    () => (Array.isArray(internalUsers) ? internalUsers : []).filter((p) => !existingParticipantIds.has(String(p?.id || ""))),
    [internalUsers, existingParticipantIds]
  );

  useEffect(() => {
    const fallbackSeconds = meetingRangeSeconds(meeting);
    const next = {};
    participantsWithKeys.forEach((p) => {
      let seconds = Number(p?.attendance_seconds);
      if (!Number.isFinite(seconds) || seconds < 0) {
        seconds = parseDurationTextToSeconds(p?.attendance_duration);
      }
      if ((!seconds || seconds < 0) && String(p?.attendance_status || "").toLowerCase() === "attended") {
        seconds = fallbackSeconds;
      }
      next[p._rowKey] = formatSecondsAsHm(Math.max(0, seconds || 0));
    });
    setTimeInputs(next);
  }, [participantsWithKeys, meeting]);

  useEffect(() => {
    const next = {};
    participantsWithKeys.forEach((p) => {
      const raw = String(p?.attendance_status || "No Response");
      next[p._rowKey] = PARTICIPATION_STATUS_OPTIONS.includes(raw) ? raw : "No Response";
    });
    setStatusInputs(next);
  }, [participantsWithKeys]);

  useEffect(() => {
    const defaultProjectId = String(
      meeting?.project_id ||
      meeting?.projectId ||
      (Array.isArray(meeting?.projectIds) ? meeting.projectIds[0] : "") ||
      ""
    ).trim();
    setInternalTimesheetProjectId(defaultProjectId);
  }, [meeting?.rawId, meeting?.project_id, meeting?.projectId, meeting?.projectIds]);

  // Epic choices are scoped to whichever project is currently selected —
  // reset the epic whenever the project changes, since a previously-picked
  // epic may not belong to the new project.
  useEffect(() => {
    setInternalTimesheetEpicId("");
    if (!internalTimesheetProjectId) {
      setTimesheetEpics([]);
      return;
    }
    setTimesheetEpicsLoading(true);
    fetch(`${API}/epics?project_id=${encodeURIComponent(internalTimesheetProjectId)}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setTimesheetEpics(Array.isArray(data) ? data : []))
      .catch(() => setTimesheetEpics([]))
      .finally(() => setTimesheetEpicsLoading(false));
  }, [internalTimesheetProjectId]);

  const editableRow = () => canEditParticipation;

  const resolvedSeconds = (p) => {
    const parsed = parseHmToSeconds(timeInputs[p._rowKey]);
    if (parsed !== null) return parsed;
    const fromRow = Number(p?.attendance_seconds);
    if (Number.isFinite(fromRow) && fromRow >= 0) return fromRow;
    return parseDurationTextToSeconds(p?.attendance_duration);
  };

  const resolvedStatus = (p) => {
    const v = String(statusInputs[p._rowKey] || p?.attendance_status || "No Response");
    return PARTICIPATION_STATUS_OPTIONS.includes(v) ? v : "No Response";
  };

  const submitTimesheet = async () => {
    if (!canSubmitTimesheet) return;
    setTsSubmitting(true);
    setTsWarn(false);
    setTsMsg("");
    try {
      const selectedProjectId = String(internalTimesheetProjectId || "").trim();
      const selectedEpicId = String(internalTimesheetEpicId || "").trim();

      // The backend silently skips any "Attended" row with 0 minutes rather
      // than erroring — if that happens to be every attended row, the only
      // feedback is a generic "No Attended participants to submit" message
      // that doesn't tell the user hours are the actual missing piece
      // (easy to miss when the hours input itself may not be visible/wide
      // enough on screen). Catch it client-side with a specific message
      // naming who needs hours entered.
      const attendedNoHours = participantsWithKeys.filter(
        (p) => String(resolvedStatus(p) || "").toLowerCase() === "attended" && resolvedSeconds(p) <= 0
      );
      if (attendedNoHours.length > 0) {
        const names = attendedNoHours.map((p) => p.name || p.email || "Unknown").join(", ");
        throw new Error(`Enter hours for attended participant(s): ${names}`);
      }

      const hasInternalAttendedRows = participantsWithKeys.some((p) => {
        if (!internalRowKeySet.has(p?._rowKey)) return false;
        return String(resolvedStatus(p) || "").toLowerCase() === "attended" && resolvedSeconds(p) > 0;
      });
      if (hasInternalAttendedRows && !selectedProjectId) {
        throw new Error("Select a project for internal users before submitting timesheet.");
      }
      if (hasInternalAttendedRows && !selectedEpicId) {
        throw new Error("Select an epic for internal users before submitting timesheet.");
      }

      let res;
      if (isMyMeeting) {
        const updates = participantsWithKeys
          .map((p) => {
            const mpId = String(p?._meetingParticipantId || "").trim();
            const status = resolvedStatus(p);
            const oldStatus = String(p?.attendance_status || "No Response");
            return { mpId, status, oldStatus };
          })
          .filter((u) => u.mpId && u.status !== u.oldStatus);

        for (const u of updates) {
          const updRes = await fetch(`${API}/meeting-participants/${u.mpId}`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attendance_status: u.status }),
          });
          if (!updRes.ok) {
            const updErr = await updRes.json().catch(() => ({}));
            throw new Error(updErr?.detail || `Failed to update participant status (${updRes.status})`);
          }
        }

        const participantMinutes = participantsWithKeys
          .filter((p) => String(resolvedStatus(p) || "").toLowerCase() === "attended")
          .map((p) => {
            const isInternal = internalRowKeySet.has(p?._rowKey);
            return {
              participant_id: p?.participant_id || "",
              minutes: Math.floor(resolvedSeconds(p) / 60),
              project_id: isInternal ? selectedProjectId : null,
              epic_id: isInternal ? selectedEpicId : null,
            };
          })
          .filter((p) => p.participant_id && p.minutes > 0);
        res = await fetch(`${API}/meetings/${meeting.rawId}/submit-timesheet`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            participant_minutes: participantMinutes,
            timesheet_project_id: selectedProjectId || null,
            timesheet_epic_id: selectedEpicId || null,
          }),
        });
      } else {
        const participantPayload = participantsWithKeys.map((p) => {
          const isInternal = internalRowKeySet.has(p?._rowKey);
          return {
            email: p.email || "",
            attendance_status: resolvedStatus(p),
            attendance_seconds: resolvedSeconds(p),
            project_id: isInternal ? selectedProjectId : null,
            epic_id: isInternal ? selectedEpicId : null,
          };
        });
        res = await fetch(`${API}/teams-meetings/submit-timesheet`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            meeting_id: meeting.rawId || meeting.key,
            title: meeting.title,
            start_utc: meeting.startUtc,
            end_utc: meeting.endUtc,
            timesheet_project_id: selectedProjectId || null,
            timesheet_epic_id: selectedEpicId || null,
            participants: participantPayload,
          }),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Error ${res.status}`);
      setTsWarn(false);
      setTsMsg(data?.message || "Timesheet submitted");
      if (data?.budget_warning) window.alert(data.budget_warning);
    } catch (e) {
      setTsWarn(true);
      setTsMsg(e?.message || "Failed to submit timesheet");
    } finally {
      setTsSubmitting(false);
    }
  };

  const addParticipants = async () => {
    if (!canManageParticipants || !meeting?.rawId) return;
    const ids = Array.isArray(addParticipantIds) ? addParticipantIds.filter(Boolean) : [];
    if (ids.length === 0) {
      setAddParticipantErr("Select at least one participant.");
      return;
    }
    setAddParticipantErr("");
    setAddingParticipants(true);
    try {
      for (const pid of ids) {
        const res = await fetch(`${API}/meetings/${meeting.rawId}/participants`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            participant_id: pid,
            role: "Required",
            attendance_status: "No Response",
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.detail || `Failed to add participant (${res.status})`);
        }
      }
      setAddParticipantIds([]);
      setTsWarn(false);
      setTsMsg("Participant(s) added.");
      await onRefreshMeetingDetails?.(meeting.rawId);
    } catch (e) {
      setTsWarn(true);
      setTsMsg("");
      setAddParticipantErr(e?.message || "Failed to add participants");
    } finally {
      setAddingParticipants(false);
    }
  };

  const statusStyle = {
    Accepted: { bg: "#E6F4EA", color: "#2E7D32" },
    Attended: { bg: "#c8e6c9", color: "#1b5e20" },
    Declined: { bg: "#FFEBEE", color: "#c62828" },
    Tentative: { bg: "#FFF4E5", color: "#c47320" },
    "No Response": { bg: "#F2F4F7", color: "#667085" },
  };

  const StatusBadge = ({ status }) => {
    const s = statusStyle[status] || statusStyle["No Response"];
    return <span className="mtv2-part-status" style={{ background: s.bg, color: s.color }}>{status}</span>;
  };

  return (
    <div className="v2drw-overlay" onClick={onClose}>
      <div className="v2drw mtv2-ts-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <div className="v2drw-title">
              {editing ? `Edit Meeting${meeting?.title ? ` — ${meeting.title}` : ""}` : (meeting?.title || "Meeting")}
            </div>
            <div className="v2drw-meta">
              <span className="mtv2-platform teams"><span className="pmark" />Teams</span>
              {statusChip(meeting?.status)}
              <span>{meetingDateLabel(meeting)} · {meeting?.from || "—"}{meeting?.to ? ` – ${meeting.to}` : ""}</span>
            </div>
          </div>
          <div className="v2drw-head-actions">
            {!editing && canSubmitTimesheet && (
              <button className="rd-ts-log-btn" type="button" onClick={submitTimesheet} disabled={tsSubmitting || meeting?._detailsLoading}>
                {tsSubmitting ? "Submitting…" : "⏱ Update Timesheet"}
              </button>
            )}
            {!editing && canEditMeeting && (
              <button
                className="rd-ts-log-btn"
                type="button"
                onClick={enterEditMode}
                disabled={meeting?._detailsLoading}
                title="Edit meeting details"
              >
                ✏️ Edit
              </button>
            )}
            {!editing && meeting?.meetingLink && (
              <a className="mtv2-join ghost" href={meeting.meetingLink} target="_blank" rel="noreferrer">Join Meeting</a>
            )}
            <button className="v2drw-close" type="button" onClick={onClose}>✕</button>
          </div>
        </div>
        {tsMsg && <div className={`v2drw-msg${tsWarn ? " warn" : ""}`}>{tsMsg}</div>}
        {editing && editError && <div className="v2drw-msg warn">{editError}</div>}
        {!editing && (
          <DrawerTabs
            tabs={[
              { key: "info", label: "Info", icon: "info" },
              {
                key: "participants",
                label: "Participants",
                icon: "users",
                badge: meeting?._detailsLoading ? "…" : (meeting?.participants || []).length,
                showZero: true,
              },
            ]}
            activeTab={tab}
            onChange={setTab}
          />
        )}
        <div className="v2drw-body">
          {editing && editForm && (
            <div className="mtv2-new-form">
              <label className="v2drw-form-label">Title *</label>
              <input
                className="v2drw-control"
                value={editForm.title}
                onChange={(e) => setEditField("title", e.target.value)}
                autoFocus
              />

              <label className="v2drw-form-label">Description</label>
              <textarea
                className="v2drw-control"
                rows={3}
                value={editForm.description}
                onChange={(e) => setEditField("description", e.target.value)}
              />

              <div className="mtv2-new-grid-2">
                <div>
                  <label className="v2drw-form-label">Type</label>
                  <select
                    className="v2drw-control"
                    value={editForm.meeting_type_id || ""}
                    onChange={(e) => setEditField("meeting_type_id", e.target.value)}
                  >
                    <option value="">— Select type —</option>
                    {meetingTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="v2drw-form-label">Status</label>
                  <select
                    className="v2drw-control"
                    value={editForm.status}
                    onChange={(e) => setEditField("status", e.target.value)}
                  >
                    {MEETING_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mtv2-new-grid-2">
                <div>
                  <label className="v2drw-form-label">Project</label>
                  <ComboBoxSingle
                    options={projectOptions}
                    value={editForm.project_id}
                    onChange={(v) => setEditField("project_id", v)}
                    placeholder="Search and select project..."
                  />
                </div>
                <div>
                  <label className="v2drw-form-label">Date *</label>
                  <input
                    className="v2drw-control"
                    type="date"
                    value={editForm.meeting_date || ""}
                    onChange={(e) => setEditField("meeting_date", e.target.value)}
                  />
                </div>
              </div>

              <div className="mtv2-new-grid-2">
                <div>
                  <label className="v2drw-form-label">Start Time *</label>
                  <input
                    className="v2drw-control"
                    type="time"
                    value={editForm.start_time || ""}
                    onChange={(e) => setEditField("start_time", e.target.value)}
                  />
                </div>
                <div>
                  <label className="v2drw-form-label">End Time</label>
                  <input
                    className="v2drw-control"
                    type="time"
                    value={editForm.end_time || ""}
                    onChange={(e) => setEditField("end_time", e.target.value)}
                  />
                </div>
              </div>

              <label className="v2drw-form-label">Meeting Link</label>
              <input
                className="v2drw-control"
                value={editForm.meeting_link || ""}
                onChange={(e) => setEditField("meeting_link", e.target.value)}
                placeholder="https://..."
              />

              <label className="v2drw-form-label">Participants</label>
              <ParticipantTagSelector
                allParticipants={internalUsers}
                selectedIds={editForm.participant_ids}
                onChange={(vals) => setEditField("participant_ids", vals)}
              />
            </div>
          )}
          {!editing && meeting?._detailsLoading && (
            <div className="mtv2-empty">Loading meeting details…</div>
          )}
          {!editing && !meeting?._detailsLoading && !!meeting?._detailsError && (
            <div className="mtv2-empty">{meeting._detailsError}</div>
          )}
          {!editing && !meeting?._detailsLoading && !meeting?._detailsError && (
            <>
          {tab === "info" ? (
            <div className="mtv2-drawer-info">
              <div><b>Date</b><span>{meetingDateLabel(meeting)}</span></div>
              <div><b>Time</b><span>{meeting?.from || "—"}{meeting?.to ? ` – ${meeting.to}` : ""}</span></div>
              <div><b>Organizer</b><span>{meeting?.owner || "—"}</span></div>
              <div><b>Location</b><span>{meeting?.project || "—"}</span></div>
              {!!meeting?.preview && <div className="full"><b>Description</b><span>{meeting.preview}</span></div>}
            </div>
          ) : (
            <div className="mtv2-part-wrap">
              {canManageParticipants && (
                <div className="mtv2-new-form" style={{ marginBottom: 12 }}>
                  <label className="v2drw-form-label">Add Participants</label>
                  <ParticipantTagSelector
                    allParticipants={addableParticipants}
                    selectedIds={addParticipantIds}
                    onChange={setAddParticipantIds}
                  />
                  {addParticipantErr && <div className="v2drw-msg warn" style={{ marginTop: 8 }}>{addParticipantErr}</div>}
                  <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                    <button
                      className="rd-ts-log-btn"
                      type="button"
                      onClick={addParticipants}
                      disabled={addingParticipants || addParticipantIds.length === 0}
                    >
                      {addingParticipants ? "Adding…" : "Add Participant(s)"}
                    </button>
                  </div>
                </div>
              )}
              {!hasParticipants ? (
                <div className="mtv2-empty">No participant information available.</div>
              ) : (
                <>
                  {!!internal.length && (
                    <>
                      <div className="mtv2-part-project-row">
                        <label className="v2drw-form-label" htmlFor="mtv2-internal-ts-project">Project for Internal Users</label>
                        <select
                          id="mtv2-internal-ts-project"
                          className="mtv2-part-project-select"
                          value={internalTimesheetProjectId}
                          onChange={(e) => setInternalTimesheetProjectId(e.target.value)}
                          disabled={!editableRow()}
                        >
                          <option value="">Select project...</option>
                          {projectOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mtv2-part-project-row">
                        <label className="v2drw-form-label" htmlFor="mtv2-internal-ts-epic">Epic for Internal Users</label>
                        <select
                          id="mtv2-internal-ts-epic"
                          className="mtv2-part-project-select"
                          value={internalTimesheetEpicId}
                          onChange={(e) => setInternalTimesheetEpicId(e.target.value)}
                          disabled={!editableRow() || !internalTimesheetProjectId || timesheetEpicsLoading}
                        >
                          <option value="">
                            {!internalTimesheetProjectId
                              ? "Select a project first..."
                              : timesheetEpicsLoading
                                ? "Loading epics…"
                                : "Select epic..."}
                          </option>
                          {epicOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mtv2-part-label">Internal Users ({internal.length})</div>
                      <table className="mtv2-part-table">
                        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Response</th></tr></thead>
                        <tbody>
                          {internal.map((p, i) => (
                            <tr key={`int-${i}`}>
                              <td>{p._displayName || "—"}</td>
                              <td>{p._email || p.email || "—"}</td>
                              <td>{p.role || "—"}</td>
                              <td>
                                <div className="mtv2-part-resp">
                                  {editableRow(p) ? (
                                    <select
                                      className="mtv2-part-status mtv2-part-status-edit"
                                      value={resolvedStatus(p)}
                                      onChange={(e) => setStatusInputs((prev) => ({ ...prev, [p._rowKey]: e.target.value }))}
                                      title="Participation status"
                                    >
                                      {PARTICIPATION_STATUS_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <StatusBadge status={resolvedStatus(p)} />
                                  )}
                                  {editableRow(p) ? (
                                    <input
                                      className="mtv2-part-time-input"
                                      value={timeInputs[p._rowKey] ?? ""}
                                      onChange={(e) => setTimeInputs((prev) => ({ ...prev, [p._rowKey]: e.target.value }))}
                                      placeholder="0:00"
                                      title="Time (h:mm)"
                                    />
                                  ) : (
                                    <span className="mtv2-part-time-view">
                                      {formatSecondsAsHm(resolvedSeconds(p))}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  {!!external.length && (
                    <>
                      <div className="mtv2-part-label">External Users ({external.length})</div>
                      <table className="mtv2-part-table">
                        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Response</th></tr></thead>
                        <tbody>
                          {external.map((p, i) => (
                            <tr key={`ext-${i}`}>
                              <td>{p._displayName || "—"}</td>
                              <td>{p._email || p.email || "—"}</td>
                              <td>{p.role || "—"}</td>
                              <td>
                                <div className="mtv2-part-resp">
                                  {editableRow(p) ? (
                                    <select
                                      className="mtv2-part-status mtv2-part-status-edit"
                                      value={resolvedStatus(p)}
                                      onChange={(e) => setStatusInputs((prev) => ({ ...prev, [p._rowKey]: e.target.value }))}
                                      title="Participation status"
                                    >
                                      {PARTICIPATION_STATUS_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <StatusBadge status={resolvedStatus(p)} />
                                  )}
                                  {editableRow(p) ? (
                                    <input
                                      className="mtv2-part-time-input"
                                      value={timeInputs[p._rowKey] ?? ""}
                                      onChange={(e) => setTimeInputs((prev) => ({ ...prev, [p._rowKey]: e.target.value }))}
                                      placeholder="0:00"
                                      title="Time (h:mm)"
                                    />
                                  ) : (
                                    <span className="mtv2-part-time-view">
                                      {formatSecondsAsHm(resolvedSeconds(p))}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              )}
            </div>
          )}
            </>
          )}
        </div>
        {editing && (
          <div className="v2drw-foot">
            <span className="v2drw-foot-spacer" />
            <button
              className="v2drw-btn-cancel"
              type="button"
              onClick={exitEditMode}
              disabled={editSaving}
            >
              Cancel
            </button>
            <button
              className="v2drw-btn-save"
              type="button"
              onClick={saveMeetingEdit}
              disabled={
                editSaving
                || !editForm
                || !editForm.title?.trim()
                || !editForm.meeting_date
                || !editForm.start_time
              }
            >
              {editSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ComboBoxSingle({ options = [], value, onChange, placeholder = "Select..." }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => String(o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => { setHighlighted(0); }, [filtered.length]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[highlighted];
    item?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const openDropdown = () => { setQuery(""); setOpen(true); };
  const closeDropdown = () => { setQuery(""); setOpen(false); };
  const commit = (opt) => { onChange(opt.value); closeDropdown(); };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { openDropdown(); return; }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) { setOpen(true); setQuery(e.key); return; }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); if (filtered[highlighted]) commit(filtered[highlighted]); }
    if (e.key === "Escape" || e.key === "Tab") closeDropdown();
  };

  return (
    <div ref={wrapRef} className={`mtv2-combobox${open ? " mtv2-combobox--open" : ""}`}>
      <div className="mtv2-combobox-input-wrap">
        <input
          ref={inputRef}
          className="mtv2-combobox-input v2drw-control"
          value={open ? query : (selected?.label || "")}
          placeholder={open ? "Type to filter..." : (selected?.label ? "" : placeholder)}
          readOnly={!open}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (!open) openDropdown(); }}
          onMouseDown={(e) => { if (!open) { e.preventDefault(); openDropdown(); } }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        <span className="mtv2-combobox-arrow">
          <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <polyline points="1 1 5 5 9 1" />
          </svg>
        </span>
      </div>
      {open && (
        <ul ref={listRef} className="mtv2-combobox-list">
          {filtered.length === 0 ? (
            <li className="mtv2-combobox-empty">No matches</li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={`${opt.value}-${i}`}
                className={`mtv2-combobox-option${i === highlighted ? " mtv2-combobox-option--highlighted" : ""}${opt.value === value ? " mtv2-combobox-option--selected" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
                onMouseEnter={() => setHighlighted(i)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function ParticipantTagSelector({ allParticipants = [], selectedIds = [], onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => allParticipants.filter((p) => selectedIds.includes(p.id)),
    [allParticipants, selectedIds]
  );

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allParticipants
      .filter((p) => !selectedIds.includes(p.id))
      .filter((p) =>
        !q
        || String(p.display_name || "").toLowerCase().includes(q)
        || String(p.user_type || "").toLowerCase().includes(q)
      )
      .slice(0, 40);
  }, [allParticipants, selectedIds, query]);

  useEffect(() => { setActiveIdx(0); }, [query, options.length]);

  const add = (p) => {
    onChange([...(selectedIds || []), p.id]);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const remove = (id) => {
    onChange((selectedIds || []).filter((x) => x !== id));
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && options[activeIdx]) add(options[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && !query && selected.length > 0) {
      remove(selected[selected.length - 1].id);
    }
  };

  return (
    <div
      className="mv-tag-input"
      ref={wrapRef}
      onClick={() => inputRef.current?.focus()}
    >
      {selected.map((p) => (
        <span key={p.id} className="mv-tag">
          {p.display_name}
          <button
            type="button"
            className="mv-tag-remove"
            onMouseDown={(e) => { e.preventDefault(); remove(p.id); }}
          >
            ✕
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="mv-tag-input__text"
        value={query}
        placeholder={selected.length === 0 ? "Search & add participants..." : ""}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => { if (!wrapRef.current?.contains(e.relatedTarget)) setOpen(false); }}
        onKeyDown={handleKeyDown}
      />
      {open && options.length > 0 && (
        <div className="mv-tag-dropdown">
          {options.map((p, i) => (
            <div
              key={p.id}
              className={`mv-tag-option${i === activeIdx ? " mv-tag-option--active" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); add(p); }}
            >
              <span className="mv-tag-opt-name">{p.display_name}</span>
              {p.user_type && <span className="mv-tag-opt-type">{p.user_type}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewMeetingDrawer({ selectedDateKey, meetingTypes, participants, projects, user, onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => ({
    title: "",
    description: "",
    meeting_type_id: "",
    meeting_date: selectedDateKey || "",
    start_time: "",
    end_time: "",
    organizer_id: user?.db_user_id || "",
    meeting_link: "",
    status: "Scheduled",
    participant_ids: [],
    project_id: "",
  }));

  useEffect(() => {
    setForm((prev) => ({ ...prev, meeting_date: selectedDateKey || prev.meeting_date || "" }));
  }, [selectedDateKey]);
  useEffect(() => {
    setForm((prev) => ({ ...prev, organizer_id: user?.db_user_id || "" }));
  }, [user?.db_user_id]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.project_id, label: `${p.project_name}${p.customer_name ? ` (${p.customer_name})` : ""}` })),
    [projects]
  );
  const canCreate = !!(
    form.title.trim()
    && form.meeting_date
    && form.start_time
  );

  const save = async () => {
    if (!form.title.trim() || !form.meeting_date || !form.start_time) {
      setError("Title, Date and Start Time are required.");
      return;
    }
    const resolvedEndTime = form.end_time || form.start_time;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/meetings`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          end_time: resolvedEndTime,
          organizer_id: user?.db_user_id || form.organizer_id || "",
          project_ids: form.project_id ? [form.project_id] : [],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Error ${res.status}`);
      onCreated?.(form.meeting_date);
      onClose?.();
    } catch (e) {
      setError(e?.message || "Failed to create meeting");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="v2drw-overlay" onClick={onClose}>
      <div className="v2drw" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <div className="v2drw-title">New Meeting</div>
            <div className="v2drw-meta">
              <span>Create meeting using existing API</span>
            </div>
          </div>
          <div className="v2drw-head-actions">
            <button className="v2drw-close" type="button" onClick={onClose}>✕</button>
          </div>
        </div>
        {error && <div className="v2drw-msg warn">{error}</div>}
        <div className="v2drw-body">
          <div className="mtv2-new-form">
            <label className="v2drw-form-label">Title *</label>
            <input className="v2drw-control" value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Meeting title..." autoFocus />

            <label className="v2drw-form-label">Description</label>
            <textarea className="v2drw-control" value={form.description} onChange={(e) => setField("description", e.target.value)} rows={3} placeholder="Agenda or purpose..." />

            <div className="mtv2-new-grid-2">
              <div>
                <label className="v2drw-form-label">Type</label>
                <select className="v2drw-control" value={form.meeting_type_id || ""} onChange={(e) => setField("meeting_type_id", e.target.value)}>
                  <option value="">— Select type —</option>
                  {meetingTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="v2drw-form-label">Status</label>
                <select className="v2drw-control" value={form.status} onChange={(e) => setField("status", e.target.value)}>
                  {MEETING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="mtv2-new-grid-2">
              <div>
                <label className="v2drw-form-label">Project</label>
                <ComboBoxSingle
                  options={projectOptions}
                  value={form.project_id}
                  onChange={(v) => setField("project_id", v)}
                  placeholder="Search and select project..."
                />
              </div>
              <div>
                <label className="v2drw-form-label">Date *</label>
                <input className="v2drw-control" type="date" value={form.meeting_date || ""} onChange={(e) => setField("meeting_date", e.target.value)} />
              </div>
            </div>

            <div className="mtv2-new-grid-2">
              <div>
                <label className="v2drw-form-label">Start Time *</label>
                <input className="v2drw-control" type="time" value={form.start_time || ""} onChange={(e) => setField("start_time", e.target.value)} />
              </div>
              <div>
                <label className="v2drw-form-label">End Time</label>
                <input className="v2drw-control" type="time" value={form.end_time || ""} onChange={(e) => setField("end_time", e.target.value)} />
              </div>
            </div>

            <label className="v2drw-form-label">Meeting Link</label>
            <input className="v2drw-control" value={form.meeting_link || ""} onChange={(e) => setField("meeting_link", e.target.value)} placeholder="https://..." />

            <label className="v2drw-form-label">Participants</label>
            <ParticipantTagSelector
              allParticipants={participants}
              selectedIds={form.participant_ids}
              onChange={(vals) => setField("participant_ids", vals)}
            />
          </div>
        </div>
        <div className="v2drw-foot">
          <span className="v2drw-foot-spacer" />
          <button className="v2drw-btn-cancel" type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="v2drw-btn-save" type="button" onClick={save} disabled={saving || !canCreate}>
            {saving ? "Saving…" : "Create Meeting"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MeetingsViewV2({ user }) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const todayKey = useMemo(() => dateKey(today), [today]);
  const currentMonthYear = useMemo(
    () => today.toLocaleString("en-US", { month: "long", year: "numeric" }),
    [today]
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const [tab, setTab] = useState("team");
  const [rangeShiftDays, setRangeShiftDays] = useState(0);
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey);
  const [myMeetings, setMyMeetings] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState("");
  const [teamsMeetings, setTeamsMeetings] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState("");
  const [nowTs, setNowTs] = useState(Date.now());
  const [dayMeetingCounts, setDayMeetingCounts] = useState({});
  const [myDayMeetingCounts, setMyDayMeetingCounts] = useState({});
  const [dayLastSyncedAt, setDayLastSyncedAt] = useState({});
  const [userTeamMeta, setUserTeamMeta] = useState({ name: "No Team", members: 0 });
  const [search, setSearch] = useState("");
  const [internalUsers, setInternalUsers] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showNewMeetingDrawer, setShowNewMeetingDrawer] = useState(false);
  const [drawerMeeting, setDrawerMeeting] = useState(null);
  const searchRef = useRef(null);
  const myCacheRef = useRef({});
  const myInFlightRef = useRef({});
  const teamsCacheRef = useRef({});
  const teamsInFlightRef = useRef({});

  const activeMeetings = tab === "my" ? myMeetings : teamsMeetings;
  const activeLoading = tab === "my" ? myLoading : teamsLoading;
  const activeError = tab === "my" ? myError : teamsError;
  const activeDayCounts = tab === "my" ? myDayMeetingCounts : dayMeetingCounts;
  const timelineMeetings = useMemo(
    () => [...myMeetings, ...teamsMeetings].sort((a, b) => (Number(a?.startMs) || 0) - (Number(b?.startMs) || 0)),
    [myMeetings, teamsMeetings]
  );
  const filteredMeetings = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return activeMeetings;
    return activeMeetings.filter((m) => {
      const platformLabel = PLATFORMS[m.platform]?.label || "";
      const hay = [
        m.id,
        m.title,
        m.owner,
        m.project,
        m.preview,
        m.rawStatus,
        platformLabel,
        m.from,
        m.to,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [activeMeetings, search]);

  const groups = useMemo(
    () => ({
      now: filteredMeetings.filter((m) => m.status === "now"),
      next: filteredMeetings.filter((m) => m.status === "next"),
      upcoming: filteredMeetings.filter((m) => m.status === "upcoming"),
      past: filteredMeetings.filter((m) => m.status === "past"),
    }),
    [filteredMeetings]
  );

  const visibleDateKeys = useMemo(() => {
    const start = addDays(today, -11 + rangeShiftDays);
    return Array.from({ length: 15 }, (_, i) => {
      const d = addDays(start, i);
      return dateKey(d);
    });
  }, [today, rangeShiftDays]);

  const stripDays = useMemo(() => {
    return visibleDateKeys.map((key) => {
      const d = dateFromKey(key);
      const m = stripMetrics(d);
      const count = Number.isFinite(activeDayCounts[key]) ? activeDayCounts[key] : 0;
      const load = m.weekend ? 0 : Math.min(100, count * 18);
      return {
        key,
        date: d,
        dow: DOW[d.getDay()],
        num: d.getDate(),
        mon: MON[d.getMonth()],
        count,
        load,
        weekend: m.weekend,
        today: key === todayKey,
      };
    });
  }, [visibleDateKeys, todayKey, activeDayCounts]);

  const selectedDate = useMemo(() => dateFromKey(selectedDateKey), [selectedDateKey]);
  const selectedStripDay = useMemo(
    () => stripDays.find((d) => d.key === selectedDateKey) || null,
    [stripDays, selectedDateKey]
  );
  const isSelectedDateToday = selectedDateKey === todayKey;
  const nowLineLeftPct = useMemo(() => {
    const now = new Date(nowTs);
    const hm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    return tm(hm);
  }, [nowTs]);
  const nowLineLabel = useMemo(
    () => new Date(nowTs).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }),
    [nowTs]
  );
  const syncStatusLabel = useMemo(
    () => formatSyncedAgo(dayLastSyncedAt[selectedDateKey], nowTs),
    [dayLastSyncedAt, selectedDateKey, nowTs]
  );
  const currentUserEmail = useMemo(
    () => String(user?.email || user?.user_name || user?.preferred_username || "").trim().toLowerCase(),
    [user]
  );
  const userTeamLabel = useMemo(() => {
    const teamName = String(userTeamMeta?.name || "No Team");
    const count = Number.isFinite(userTeamMeta?.members) ? userTeamMeta.members : 0;
    return `${teamName} · ${count} MEMBERS`;
  }, [userTeamMeta]);
  const selectedBookedMinutes = useMemo(
    () => bookedMinutesFromMeetings(activeMeetings, currentUserEmail),
    [activeMeetings, currentUserEmail]
  );
  const loadLabel = useMemo(() => {
    const targetMins = 9 * 60;
    const hours = selectedBookedMinutes / 60;
    const pct = targetMins > 0 ? Math.round((selectedBookedMinutes / targetMins) * 100) : 0;
    return `${hours.toFixed(1)}H / 9H BOOKED (${pct}%)`;
  }, [selectedBookedMinutes]);
  const loadPct = useMemo(() => {
    const targetMins = 9 * 60;
    return targetMins > 0 ? Math.round((selectedBookedMinutes / targetMins) * 100) : 0;
  }, [selectedBookedMinutes]);
  const loadChipClass = useMemo(() => {
    if (loadPct > 50) return "mtv2-load-chip mtv2-load-chip--red";
    if (loadPct > 25) return "mtv2-load-chip mtv2-load-chip--amber";
    return "mtv2-load-chip";
  }, [loadPct]);
  const conflictsCount = useMemo(() => countMeetingConflicts(activeMeetings), [activeMeetings]);

  const refreshMeetingDetails = useCallback(async (rawMeetingId) => {
    const id = String(rawMeetingId || "").trim();
    if (!id) return;
    try {
      const res = await fetch(`${API}/meetings/${id}`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Error ${res.status}`);
      }
      const detail = await res.json();
      const participants = normalizeMeetingDetailParticipants(detail?.participants || []);
      setDrawerMeeting((curr) => {
        const sameMeeting = !!curr && (String(curr.rawId || "") === id);
        if (!sameMeeting) return curr;
        return {
          ...curr,
          rawStatus: detail?.status || curr.rawStatus,
          organizerId: detail?.organizer_id || curr.organizerId,
          owner: detail?.organizer_name || curr.owner,
          project: detail?.location || detail?.meeting_type_name || curr.project,
          preview: detail?.description || curr.preview,
          meetingLink: detail?.meeting_link || curr.meetingLink,
          participants,
          source: "my",
          // Raw fields used by the edit form
          meetingTypeId: detail?.meeting_type_id || "",
          description: detail?.description || "",
          startTime: detail?.start_time || "",
          endTime: detail?.end_time || "",
          meetingDate: detail?.meeting_date || curr.meetingDate || "",
          projectIds: (detail?.projects || [])
            .map((p) => p?.project_id)
            .filter(Boolean),
          _detailsLoading: false,
          _detailsError: "",
        };
      });
    } catch (e) {
      setDrawerMeeting((curr) => {
        const sameMeeting = !!curr && (String(curr.rawId || "") === id);
        if (!sameMeeting) return curr;
        return {
          ...curr,
          _detailsLoading: false,
          _detailsError: e?.message || "Failed to load meeting details",
        };
      });
    }
  }, []);

  const openMeetingDetails = (m) => {
    // Keep drawer payload lightweight and deterministic to prevent UI stalls.
    const baseMeeting = {
      key: m?.key || "",
      rawId: m?.rawId || "",
      organizerId: m?.organizerId || "",
      organizerEmail: m?.organizerEmail || "",
      rawStatus: m?.rawStatus || "",
      title: m?.title || "(No title)",
      from: m?.from || "",
      to: m?.to || "",
      status: m?.status || "upcoming",
      owner: m?.owner || "—",
      project: m?.project || "—",
      preview: typeof m?.preview === "string" ? m.preview : "",
      meetingLink: m?.meetingLink || "",
      meetingDate: m?.meetingDate || "",
      startUtc: m?.startUtc || "",
      endUtc: m?.endUtc || "",
      participants: Array.isArray(m?.participants) ? m.participants : [],
      source: m?.source || "team",
      _detailsLoading: tab === "my" && !!m?.rawId,
      _detailsError: "",
    };
    setDrawerMeeting(baseMeeting);

    if (tab !== "my" || !m?.rawId) return;

    refreshMeetingDetails(m.rawId);
  };

  const moveStripByWeek = (delta) => {
    setRangeShiftDays((s) => s + delta);
    setSelectedDateKey((k) => dateKey(addDays(dateFromKey(k), delta)));
  };

  const resetStripToToday = () => {
    setRangeShiftDays(0);
    setSelectedDateKey(todayKey);
  };

  const teamsSessionExpired = /401|expired|unauthorized|uthori[sz]ed|forbidden|sign in|login|re-?login|re-?auth/i.test(teamsError);

  const handleCreatedMeeting = async (createdDate) => {
    const targetDate = createdDate || selectedDateKey;
    if (targetDate === selectedDateKey) {
      await fetchMyMeetingsForDate(targetDate, { force: true });
      return;
    }
    try {
      const normalized = await loadMyMeetingsForDate(targetDate, { force: true });
      setMyDayMeetingCounts((prev) => ({ ...prev, [targetDate]: normalized.length }));
      const syncedAt = myCacheRef.current?.[targetDate]?.fetchedAt || Date.now();
      setDayLastSyncedAt((prev) => ({ ...prev, [targetDate]: syncedAt }));
    } catch {
      // no-op: creation already succeeded; background refresh failed
    }
  };

  const loadMyMeetingsForDate = async (dateStr, { force = false } = {}) => {
    const now = Date.now();
    if (force) {
      delete myCacheRef.current[dateStr];
      delete myInFlightRef.current[dateStr];
    }
    const cached = myCacheRef.current[dateStr];
    if (cached && now - cached.fetchedAt < MY_MEETINGS_CACHE_TTL_MS) return cached.data;
    if (myInFlightRef.current[dateStr]) return myInFlightRef.current[dateStr];
    const request = (async () => {
      const params = new URLSearchParams();
      params.set("date_from", dateStr);
      params.set("date_to", dateStr);
      const res = await fetch(`${API}/meetings?${params.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      const normalized = normalizeMyMeetings(data);
      myCacheRef.current = {
        ...myCacheRef.current,
        [dateStr]: { data: normalized, fetchedAt: Date.now() },
      };
      return normalized;
    })();
    myInFlightRef.current[dateStr] = request;
    try {
      return await request;
    } finally {
      delete myInFlightRef.current[dateStr];
    }
  };

  const fetchMyMeetingsForDate = async (dateStr, { force = false } = {}) => {
    setMyLoading(true);
    setMyError("");
    try {
      const normalized = await loadMyMeetingsForDate(dateStr, { force });
      const syncedAt = myCacheRef.current?.[dateStr]?.fetchedAt || Date.now();
      setMyMeetings(normalized);
      setMyDayMeetingCounts((prev) => ({ ...prev, [dateStr]: normalized.length }));
      setDayLastSyncedAt((prev) => ({ ...prev, [dateStr]: syncedAt }));
    } catch (e) {
      setMyMeetings([]);
      const msg = e?.message || "Failed to load meetings";
      setMyError(msg);
    } finally {
      setMyLoading(false);
    }
  };

  const loadTeamsMeetingsForDate = async (dateStr, { force = false } = {}) => {
    const now = Date.now();
    if (force) {
      delete teamsCacheRef.current[dateStr];
      delete teamsInFlightRef.current[dateStr];
    }
    const cached = teamsCacheRef.current[dateStr];
    if (cached && now - cached.fetchedAt < TEAMS_CACHE_TTL_MS) {
      return cached.data;
    }
    if (teamsInFlightRef.current[dateStr]) {
      return teamsInFlightRef.current[dateStr];
    }
    const request = (async () => {
      const res = await fetch(`${API}/teams-meetings?for_date=${dateStr}`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      const normalized = normalizeTeamsMeetings(data);
      teamsCacheRef.current = {
        ...teamsCacheRef.current,
        [dateStr]: { data: normalized, fetchedAt: Date.now() },
      };
      return normalized;
    })();
    teamsInFlightRef.current[dateStr] = request;
    try {
      return await request;
    } finally {
      delete teamsInFlightRef.current[dateStr];
    }
  };

  const fetchTeamsMeetingsForDate = async (dateStr, { force = false } = {}) => {
    setTeamsLoading(true);
    setTeamsError("");
    try {
      const normalized = await loadTeamsMeetingsForDate(dateStr, { force });
      const syncedAt = teamsCacheRef.current?.[dateStr]?.fetchedAt || Date.now();
      setTeamsMeetings(normalized);
      setDayMeetingCounts((prev) => ({ ...prev, [dateStr]: normalized.length }));
      setDayLastSyncedAt((prev) => ({ ...prev, [dateStr]: syncedAt }));
    } catch (e) {
      setTeamsMeetings([]);
      const msg = e?.message || "Failed to load Teams meetings";
      setTeamsError(/401/.test(msg) ? "Microsoft session expired. Sign in again to load Teams meetings." : msg);
    } finally {
      setTeamsLoading(false);
    }
  };

  useEffect(() => {
    fetchMyMeetingsForDate(selectedDateKey);
    fetchTeamsMeetingsForDate(selectedDateKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateKey]);

  useEffect(() => {
    let cancelled = false;
    const keys = visibleDateKeys;
    (async () => {
      const pairs = await Promise.all(keys.map(async (key) => {
        try {
          const list = await loadMyMeetingsForDate(key);
          return [key, list.length];
        } catch {
          return [key, 0];
        }
      }));
      if (cancelled) return;
      setMyDayMeetingCounts((prev) => {
        const next = { ...prev };
        let changed = false;
        pairs.forEach(([key, count]) => {
          if (next[key] !== count) {
            next[key] = count;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleDateKeys]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTs(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key || "").toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [partsRes, typesRes, projectsRes] = await Promise.all([
          fetch(`${API}/meeting-participants-list`, { credentials: "include" }),
          fetch(`${API}/meeting-types`, { credentials: "include" }),
          fetch(`${API}/customers-projects?active_only=true`, { credentials: "include" }),
        ]);
        const [parts, types, customersProjects] = await Promise.all([
          partsRes.ok ? partsRes.json() : [],
          typesRes.ok ? typesRes.json() : [],
          projectsRes.ok ? projectsRes.json() : [],
        ]);
        if (cancelled) return;
        setInternalUsers(Array.isArray(parts) ? parts : []);
        setMeetingTypes(Array.isArray(types) ? types : []);
        const flat = [];
        (Array.isArray(customersProjects) ? customersProjects : []).forEach((c) => {
          (Array.isArray(c?.projects) ? c.projects : []).forEach((p) => {
            flat.push({
              project_id: p.project_id,
              project_name: p.project_name,
              customer_name: c.customer_name,
            });
          });
        });
        setProjects(flat);
      } catch {
        if (!cancelled) {
          setInternalUsers([]);
          setMeetingTypes([]);
          setProjects([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const uid = user?.db_user_id;
    if (!uid) {
      setUserTeamMeta({ name: "No Team", members: 0 });
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const teamsRes = await fetch(`${API}/users/${uid}/teams`, { credentials: "include" });
        if (!teamsRes.ok) throw new Error(`HTTP ${teamsRes.status}`);
        const teams = await teamsRes.json();
        const list = Array.isArray(teams) ? teams : [];
        const primary = list[0];
        if (!primary) {
          if (!cancelled) setUserTeamMeta({ name: "No Team", members: 0 });
          return;
        }
        const teamId = primary.id;
        const teamName = primary.team_name || "Unknown Team";
        try {
          const membersRes = await fetch(`${API}/teams/${teamId}/members`, { credentials: "include" });
          if (!membersRes.ok) throw new Error(`HTTP ${membersRes.status}`);
          const members = await membersRes.json();
          const memberCount = Array.isArray(members) ? members.length : 0;
          if (!cancelled) setUserTeamMeta({ name: teamName, members: memberCount });
        } catch {
          if (!cancelled) setUserTeamMeta({ name: teamName, members: 0 });
        }
      } catch {
        if (!cancelled) setUserTeamMeta({ name: "No Team", members: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.db_user_id]);

  return (
    <div className="mtv2-root" style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}>
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed((c) => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="mtv2-shell">
        <div className="page-head">
          <h1>
            Meetings <em>{currentMonthYear}</em>
          </h1>
          <div className="page-head-actions">
            {tab === "my" ? (
              <button className="mtv2-sync v2-btn-primary" onClick={() => setShowNewMeetingDrawer(true)}>
                + New Meeting
              </button>
            ) : (
              <button
                className="mtv2-sync v2-btn-primary"
                onClick={() => fetchTeamsMeetingsForDate(selectedDateKey, { force: true })}
                disabled={activeLoading}
              >
                {activeLoading ? "Syncing…" : "↻ Sync again"}
              </button>
            )}
          </div>
        </div>

        <div className="page-toolbar">
          <div className="page-tabs">
            {[
              { key: "my", label: "My Meetings", ct: "07" },
              { key: "team", label: "Team's Meetings", ct: "14" },
            ].map((t) => (
              <button
                key={t.key}
                className={`page-tab${tab === t.key ? " page-tab--active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label} <span className="mtv2-ct">{t.ct}</span>
              </button>
            ))}
          </div>
          <div className="page-toolbar-right">
            <div className="mtv2-toolbar-search">
              <svg className="mtv2-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchRef}
                className="mtv2-search-input"
                placeholder="Search meetings…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="mtv2-search-kbd">⌘K</span>
            </div>
          </div>
        </div>

        <div className="mtv2-scroll">
          <DateStrip
            selectedDate={selectedDate}
            selectedDateKey={selectedDateKey}
            stripDays={stripDays}
            onSelectDateKey={setSelectedDateKey}
            onMoveWeek={moveStripByWeek}
            onResetToToday={resetStripToToday}
          />

          <div className="mtv2-meta">
            <div className="left">
              <span><span className="dot" />{syncStatusLabel}</span>
              <span><span className="k">TEAM</span>{userTeamLabel}</span>
              <span className={loadChipClass}><span className="k">LOAD</span>{loadLabel}</span>
              <span><span className="k">CONFLICTS</span>{conflictsCount}</span>
            </div>
            <div className="right">
              {selectedDate.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" }).toUpperCase()}
              {" · "}
              {selectedStripDay?.count ?? 0}
              {" MEETINGS · 3 OWNERS"}
            </div>
          </div>

          <div className="mtv2-timeline">
            <div className="mtv2-timeline-head">
              <span>Day timeline · <b>06:00 → 22:00</b></span>
              <span>teams · zoom · meet · in-person</span>
            </div>
            <div className="mtv2-tl">
              <div className="mtv2-tl-hours">
                {["06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21"].map((h) => (
                  <div key={h}>{h}</div>
                ))}
              </div>
              {timelineMeetings.map((m) => (
                <div
                  key={`tl-${m.key}`}
                  className={`mtv2-tl-block ${PLATFORMS[m.platform].cls}${m.status === "now" ? " now" : ""}`}
                  style={{ left: `${tm(m.from)}%`, width: `${tm(m.to) - tm(m.from)}%` }}
                  title={m.title}
                >
                  {m.from} {m.title.split("·")[0].trim()}
                </div>
              ))}
              {isSelectedDateToday && (
                <div className="mtv2-tl-now" style={{ left: `${nowLineLeftPct}%` }}>
                  <span className="mtv2-tl-now-label">NOW · {nowLineLabel}</span>
                </div>
              )}
            </div>
          </div>

          <div className="mtv2-list">
            {activeError && (
              <div className="mtv2-freeslot">
                <span><b>Unable to load meetings ·</b> {activeError}</span>
                {tab !== "my" && teamsSessionExpired ? (
                  <a href="#!" onClick={(e) => { e.preventDefault(); startMicrosoftLoginForTeams(selectedDateKey); }}>
                    Sign in with Microsoft
                  </a>
                ) : (
                  <a href="#!" onClick={(e) => {
                    e.preventDefault();
                    if (tab === "my") fetchMyMeetingsForDate(selectedDateKey, { force: true });
                    else fetchTeamsMeetingsForDate(selectedDateKey, { force: true });
                  }}>
                    Retry
                  </a>
                )}
              </div>
            )}
            {!activeError && !activeLoading && filteredMeetings.length === 0 && (
              <div className="mtv2-freeslot">
                {search.trim() ? (
                  <>
                    <span><b>No matching meetings ·</b> Try a different search keyword</span>
                    <a href="#!" onClick={(e) => { e.preventDefault(); setSearch(""); }}>
                      Clear search
                    </a>
                  </>
                ) : (
                  <>
                    <span><b>No meetings ·</b> No meetings on {toDateLabel(selectedDateKey)}</span>
                    <a href="#!" onClick={(e) => {
                      e.preventDefault();
                      if (tab === "my") fetchMyMeetingsForDate(selectedDateKey, { force: true });
                      else fetchTeamsMeetingsForDate(selectedDateKey, { force: true });
                    }}>
                      Sync now
                    </a>
                  </>
                )}
              </div>
            )}
            {groups.now.length > 0 && (
              <>
                <div className="mtv2-group-head">Live now · <span className="ct">{groups.now.length}</span></div>
                {groups.now.map((m) => <MeetingRow key={m.key} m={m} onOpenDetails={openMeetingDetails} />)}
              </>
            )}

            {groups.next.length > 0 && (
              <>
                <div className="mtv2-group-head">Up next · <span className="ct">{groups.next.length}</span> · starts in 18m</div>
                {groups.next.map((m) => <MeetingRow key={m.key} m={m} onOpenDetails={openMeetingDetails} />)}
              </>
            )}

            {groups.upcoming.length > 0 && (
              <>
                <div className="mtv2-group-head">Later today · <span className="ct">{groups.upcoming.length}</span></div>
                {groups.upcoming.map((m) => <MeetingRow key={m.key} m={m} onOpenDetails={openMeetingDetails} />)}
              </>
            )}

            {groups.past.length > 0 && (
              <>
                <div className="mtv2-group-head">Completed · <span className="ct">{groups.past.length}</span></div>
                {groups.past.map((m) => <MeetingRow key={m.key} m={m} onOpenDetails={openMeetingDetails} />)}
              </>
            )}
          </div>
        </div>
      </div>
      {drawerMeeting && (
        <TeamsMeetingDrawer
          meeting={drawerMeeting}
          internalUsers={internalUsers}
          meetingTypes={meetingTypes}
          projects={projects}
          user={user}
          onRefreshMeetingDetails={refreshMeetingDetails}
          onAfterEdit={handleCreatedMeeting}
          onClose={() => setDrawerMeeting(null)}
        />
      )}
      {showNewMeetingDrawer && (
        <NewMeetingDrawer
          selectedDateKey={selectedDateKey}
          meetingTypes={meetingTypes}
          participants={internalUsers}
          projects={projects}
          user={user}
          onClose={() => setShowNewMeetingDrawer(false)}
          onCreated={handleCreatedMeeting}
        />
      )}
    </div>
  );
}
